/* eslint-disable no-console */
/**
 * Thin logger wrapper with environment-aware log levels.
 * Suppresses DEBUG and INFO in production, DEBUG in staging.
 * When a shell logger is provided (via setShellLogger), delegates to it
 * so that Sentry breadcrumbs and remote transports are handled by the shell.
 * Falls back to local console logging in standalone mode.
 *
 * @module logger
 */

const LOG_LEVELS = { DEBUG: 0, INFO: 1, WARN: 2, ERROR: 3, SILENT: 4 };

/**
 * Resolves the minimum log level from the current environment.
 * @returns {number} Numeric log level threshold.
 */
const getLogLevel = () => {
    try {
        const env = process.env.NODE_ENV;
        if (env === "production") return LOG_LEVELS.WARN;
        if (env === "staging") return LOG_LEVELS.INFO;
        return LOG_LEVELS.DEBUG;
    } catch {
        return LOG_LEVELS.DEBUG;
    }
};

const currentLevel = getLogLevel();

let _shellLogger = null;

/**
 * Store the shell-provided logger so all MFE logging delegates to it.
 * The shell logger already has Sentry breadcrumb support built in.
 * @param {Object} shellLogger - Logger with debug/info/warn/error methods.
 */
export const setShellLogger = shellLogger => {
    if (shellLogger && typeof shellLogger.error === "function") {
        _shellLogger = shellLogger;
    }
};

const logger = {
    /**
     * Detailed processing steps, payload details — suppressed outside development.
     * @param {...*} args - Values to log.
     */
    debug: (...args) => {
        if (_shellLogger) {
            _shellLogger.debug(...args);
            return;
        }
        if (currentLevel <= LOG_LEVELS.DEBUG) {
            console.log("[DEBUG]", ...args);
        }
    },

    /**
     * Request lifecycle, startup events, informational messages.
     * @param {...*} args - Values to log.
     */
    info: (...args) => {
        if (_shellLogger) {
            _shellLogger.info(...args);
            return;
        }
        if (currentLevel <= LOG_LEVELS.INFO) {
            console.info("[INFO]", ...args);
        }
    },

    /**
     * Recoverable issues, 4xx responses, degraded state.
     * @param {...*} args - Values to log.
     */
    warn: (...args) => {
        if (_shellLogger) {
            _shellLogger.warn(...args);
            return;
        }
        if (currentLevel <= LOG_LEVELS.WARN) {
            console.warn("[WARN]", ...args);
        }
    },

    /**
     * Exceptions, execution failures, 5xx responses.
     * @param {...*} args - Values to log.
     */
    error: (...args) => {
        if (_shellLogger) {
            _shellLogger.error(...args);
            return;
        }
        if (currentLevel <= LOG_LEVELS.ERROR) {
            console.error("[ERROR]", ...args);
        }
    },
};

export default logger;
